import ApiClient from '@/domain/core/utils/apiClient'
import client_core from '@/services/clients/client_core'
const client = new ApiClient(client_core)

const endpoints = {
  list: '/dashboard/api/v3/email-templates/{scope}/templates/',
  detail: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/',
  availableTypes: '/dashboard/api/v3/email-templates/{scope}/templates/available-types/',
  availableParameters: '/dashboard/api/v3/email-templates/{scope}/templates/parameters/',
  debugLogs: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/',
  debugLogsExport: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/export/',
  debugLogsSendToEmails: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/send-to-emails/',
  debugLogsReplaySelected: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/replay-selected/',
  debugLogsDeleteSelected: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/delete-selected/',
  debugLogsMarkAsSent: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/mark-as-sent/'
}

/**
 * Build scope path for API endpoints
 * @param {Object} scope - {type: 'global'|'contract-manager'|'event-owner'|'pica-service', id: number}
 * @returns {string} - Scope path for URL
 */
const buildScope = (scope) => {
  if (scope.type === 'global') {
    return 'global'
  } else if (scope.type === 'contract-manager') {
    return `contract-manager/${scope.id}`
  } else if (scope.type === 'event-owner') {
    return `event-owner/${scope.id}`
  } else if (scope.type === 'pica-service') {
    return `pica-service/${scope.id}`
  }
  throw new Error(`Invalid scope type: ${scope.type}`)
}

const orderingFieldsMap = {
  email_type: 'email_type',
  delivery_service: 'delivery_service',
  debug_mode: 'debug_mode',
  enabled: 'enabled'
}

const debugLogsOrderingFieldsMap = {
  recipient_email: 'recipient_email',
  created_at: 'created_at',
  sent_at: 'sent_at',
  status: 'status'
}

const filterFieldsMap = {
  // frontend key → backend param (pass as-is if not mapped)
}

/**
 * Build query params from filters, pagination and sorting
 * @param {Object} filters
 * @param {Object} pagination
 * @param {Object} sorting
 * @param {Object} orderingMap - mapping frontend column → backend ordering field
 * @returns {Object}
 */
const buildQueryParams = (filters = {}, pagination = {}, sorting = {}, orderingMap = {}) => {
  const orderingColumn = sorting.column ? orderingMap[sorting.column] : null
  const ordering = orderingColumn
    ? (sorting.asc ? `-${orderingColumn}` : orderingColumn)
    : ''

  const mappedFilters = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value === '' || value === null || value === undefined) continue
    const backendKey = filterFieldsMap[key] || key
    mappedFilters[backendKey] = value
  }

  return {
    ...mappedFilters,
    page_size: pagination.page_size ?? 20,
    page: pagination.page ?? 1,
    ...(ordering ? { ordering } : {})
  }
}

export default {
  /**
   * Fetch all email templates for a given scope
   * @param {Object} scope
   * @param {Object} filters
   * @param {Object} pagination
   * @param {Object} sorting
   * @returns {Promise<Object>} - {results, pagination}
   */
  async fetchEmailTemplates(scope, filters = {}, pagination = {}, sorting = {}) {
    const scopePath = buildScope(scope)
    const url = endpoints.list.replace('{scope}', scopePath)
    const params = buildQueryParams(filters, pagination, sorting, orderingFieldsMap)

    const result = await client.get(url, params)

    return {
      results: result.results || [],
      pagination: {
        num_pages: result.pagination?.num_pages || 1,
        page_size: result.pagination?.page_size || params.page_size,
        page: result.pagination?.page_number || params.page,
        total: result.pagination?.total || 0
      }
    }
  },

  /**
   * Fetch a single email template by type
   * @param {Object} scope
   * @param {string} emailType
   * @returns {Promise<Object>}
   */
  async fetchEmailTemplate(scope, emailType) {
    const scopePath = buildScope(scope)
    const url = endpoints.detail
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    return await client.get(url)
  },

  /**
   * Create a new email template
   * @param {Object} scope
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  async createEmailTemplate(scope, data) {
    const scopePath = buildScope(scope)
    const url = endpoints.list.replace('{scope}', scopePath)
    return await client.post(url, data)
  },

  /**
   * Update an existing email template
   * @param {Object} scope
   * @param {string} emailType
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  async updateEmailTemplate(scope, emailType, data) {
    const scopePath = buildScope(scope)
    const url = endpoints.detail
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    return await client.put(url, data)
  },

  /**
   * Delete an email template
   * @param {Object} scope
   * @param {string} emailType
   * @returns {Promise<void>}
   */
  async deleteEmailTemplate(scope, emailType) {
    const scopePath = buildScope(scope)
    const url = endpoints.detail
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    return await client.delete(url)
  },

  /**
   * Fetch available email types for a given scope
   * @param {Object} scope
   * @returns {Promise<Array>} - Array of available email types with their configurations
   */
  async fetchAvailableTypes(scope) {
    const scopePath = buildScope(scope)
    const url = endpoints.availableTypes.replace('{scope}', scopePath)
    return await client.get(url)
  },

  /**
   * Fetch available parameters for an email type
   * @param {Object} scope
   * @param {string} emailType
   * @returns {Promise<Object>} - Object with parameter definitions
   */
  async fetchAvailableParameters(scope, emailType) {
    const scopePath = buildScope(scope)
    const url = `${endpoints.availableParameters.replace('{scope}', scopePath)}?email_type=${emailType}`
    return await client.get(url)
  },

  /**
   * Fetch debug email logs for a given email template
   * @param {Object} scope
   * @param {string} emailType
   * @param {Object} filters
   * @param {Object} pagination
   * @param {Object} sorting
   * @returns {Promise<Object>} - {results, pagination} in AjaxTable format
   */
  async fetchDebugLogs(scope, emailType, filters = {}, pagination = {}, sorting = {}) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogs
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    const params = buildQueryParams(filters, pagination, sorting, debugLogsOrderingFieldsMap)

    const result = await client.get(url, params)

    return {
      results: result.results || [],
      pagination: {
        num_pages: result.pagination?.num_pages || 1,
        page_size: result.pagination?.page_size || params.page_size,
        page: result.pagination?.page_number || params.page,
        total: result.pagination?.total || 0
      }
    }
  },

  /**
   * Export debug logs to Excel file
   * @param {Object} scope
   * @param {string} emailType
   * @param {Object} filters - Current table filters to apply to export
   * @returns {Promise<void>} - Downloads file automatically
   */
  async exportDebugLogs(scope, emailType, filters = {}) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogsExport
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)

    const params = {}
    for (const [key, value] of Object.entries(filters)) {
      if (value === '' || value === null || value === undefined) continue
      params[key] = value
    }

    await client.download(url, params)
  },

  /**
   * Send emails to specific email addresses (creates debug logs)
   * @param {Object} scope
   * @param {string} emailType
   * @param {Array<string>} emailAddresses - Array of email addresses
   * @param {boolean} sendOnlyPending - Only send to recipients who haven't received the email yet
   * @param {Object} filters - Current table filters to apply
   * @returns {Promise<Object>} - {success_count, error_count, errors, total_processed}
   */
  async sendToEmails(scope, emailType, emailAddresses, sendOnlyPending = true, filters = {}) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogsSendToEmails
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)

    const body = {
      email_addresses: emailAddresses,
      send_only_pending: sendOnlyPending
    }

    // Add non-empty filters to body
    for (const [key, value] of Object.entries(filters)) {
      if (value !== '' && value !== null && value !== undefined) {
        body[key] = value
      }
    }

    return await client.post(url, body)
  },

  /**
   * Replay selected debug logs by IDs
   * @param {Object} scope
   * @param {string} emailType
   * @param {Array<number>} logIds - Array of log IDs to replay
   * @param {string|null} recipientAddress - Optional recipient address override
   * @returns {Promise<Object>} - {success_count, error_count, errors, total_processed}
   */
  async replaySelectedDebugLogs(scope, emailType, logIds, recipientAddress = null) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogsReplaySelected
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    const body = { log_ids: logIds }
    if (recipientAddress) {
      body.recipient_address = recipientAddress
    }
    return await client.post(url, body)
  },

  /**
   * Delete selected debug logs by IDs
   * @param {Object} scope
   * @param {string} emailType
   * @param {Array<number>} logIds - Array of log IDs to delete
   * @returns {Promise<Object>} - {deleted_count, message}
   */
  async deleteSelectedDebugLogs(scope, emailType, logIds) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogsDeleteSelected
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    return await client.post(url, { log_ids: logIds })
  },

  /**
   * Mark debug logs as sent without actually sending them
   * @param {Object} scope
   * @param {string} emailType
   * @param {Array<number>|null} logIds - Array of log IDs to mark, or null if using 'all'
   * @param {boolean} all - If true, mark all logs as sent
   * @returns {Promise<Object>} - {updated_count, message}
   */
  async markDebugLogsAsSent(scope, emailType, logIds = null, all = false) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogsMarkAsSent
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    const body = all ? { all: true } : { log_ids: logIds }
    return await client.post(url, body)
  }
}
